import React from "react";
import type { Project } from "@/data/projects";
import type { ImageSources } from "@/images/generated/manifest";
import "./DeviceComposite.scss";

interface ShotProps {
  img: ImageSources;
  /** `sizes` for the responsive desktop shot (640w/1280w). */
  sizes?: string;
  alt: string;
  priority?: boolean;
  /** Desktop shots ship 640w + 1280w WebP; phone shots are a single ~390w file. */
  responsive: boolean;
}

/** One screenshot: WebP with a JPEG fallback. */
const Shot: React.FC<ShotProps> = ({ img, sizes, alt, priority, responsive }) => (
  <picture className="dc-shot">
    <source
      type="image/webp"
      srcSet={responsive ? `${img.webp} 640w, ${img.webp2x} 1280w` : img.webp2x}
      sizes={responsive ? sizes : undefined}
    />
    <img
      src={img.fallback}
      alt={alt}
      width={img.width}
      height={img.height}
      decoding="async"
      loading={priority ? undefined : "lazy"}
      // Lowercase: React 18 doesn't know `fetchPriority` and would warn.
      {...(priority ? { fetchpriority: "high" } : {})}
    />
  </picture>
);

interface DeviceCompositeProps {
  project: Project;
  /**
   * Visual context — each sizes the frames in CSS. "panel" is the portfolio's
   * expanded spine (≥1024px) / band (<1024px); "hero" is the case-study hero.
   */
  variant: "panel" | "hero";
  /** `sizes` hint for the desktop screenshot. */
  desktopSizes?: string;
  /** Load the main screenshot eagerly with fetchpriority=high (LCP hero). */
  priority?: boolean;
  /** Extra class on the main frame (browser for duo projects, phone otherwise). */
  mainClassName?: string;
  /** Alt text for the main screenshot; decorative ("") by default. */
  mainAlt?: string;
}

/**
 * The live product on its own brand color: a browser window with the desktop
 * screenshot and an overlapping phone with the mobile one ("duo"), or a single
 * larger phone for phone-only products. Real screenshots, no generated art.
 */
const DeviceComposite: React.FC<DeviceCompositeProps> = ({
  project,
  variant,
  desktopSizes = "50vw",
  priority = false,
  mainClassName,
  mainAlt = "",
}) => {
  const { desktop, mobile } = project.screens;
  const duo = project.device === "duo" && !!desktop;
  const main = (extra: string) => [extra, mainClassName].filter(Boolean).join(" ");

  return (
    <div className={`dc dc--${variant} dc--${duo ? "duo" : "phone"}`}>
      {duo && desktop ? (
        <>
          <div className={main("dc-browser")}>
            <span className="dc-browser-bar" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <Shot img={desktop} sizes={desktopSizes} alt={mainAlt} priority={priority} responsive />
          </div>
          <div className="dc-phone dc-phone--side">
            <Shot img={mobile} alt="" responsive={false} />
          </div>
        </>
      ) : (
        <div className={main("dc-phone dc-phone--solo")}>
          <Shot img={mobile} alt={mainAlt} priority={priority} responsive={false} />
        </div>
      )}
    </div>
  );
};

export default DeviceComposite;
